class HappyTummies extends HTMLElement {
  constructor() {
    super();

    this.sectionObserver = null;
    this.imageObserver = null;
    this.animationId = null;

    this._resizeRaf = null;
  }

  connectedCallback() {
    this.initSectionLazyLoading();
    this.initImageLazyLoading();
    this.initCarousel();
  }

  disconnectedCallback() {
    if (this.sectionObserver) this.sectionObserver.disconnect();
    if (this.imageObserver) this.imageObserver.disconnect();
    if (this.animationId) cancelAnimationFrame(this.animationId);
    if (this._resizeRaf) cancelAnimationFrame(this._resizeRaf);
    window.removeEventListener("resize", this._onResize);
  }

  // Section Lazy Loading
  initSectionLazyLoading() {
    const sections = this.querySelectorAll("[data-lazy-section]");
    if (!sections.length) return;

    this.sectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("loaded");
            this.sectionObserver.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: "50px" }
    );

    sections.forEach((section) => this.sectionObserver.observe(section));
  }

  // Image Lazy Loading
  initImageLazyLoading() {
    const images = this.querySelectorAll("[data-lazy-image]");
    if (!images.length) return;

    this.imageObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("loaded");
            this.imageObserver.unobserve(entry.target);
          }
        });
      },
      { threshold: 0, rootMargin: "50px" }
    );

    images.forEach((img) => this.imageObserver.observe(img));
  }

  initCarousel() {
    const wrapper = this.querySelector("[data-carousel]");
    const cards = wrapper?.querySelector(".testimonial-cards");
    if (!wrapper || !cards) return;

    const prefersReducedMotion =
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (prefersReducedMotion) return;

    const isTouchDevice = "ontouchstart" in window || navigator.maxTouchPoints > 0;

    let scrollPosition = 0;
    let isUserInteracting = false;
    let userInteractionTimeout = null;

    const scrollSpeed = 0.5;

    const getGapPx = () => {
      const cs = window.getComputedStyle(cards);
      // gap can be "40px" or "40px 40px"
      const raw = (cs.gap || cs.columnGap || "0px").toString().trim();
      const first = raw.split(" ")[0];
      const n = parseFloat(first);
      return Number.isFinite(n) ? n : 0;
    };

    const getCardWidth = () => {
      const first = cards.children[0];
      return first ? first.getBoundingClientRect().width : 280;
    };

    const waitForImages = async () => {
      const imgs = Array.from(cards.querySelectorAll("img"));
      if (!imgs.length) return;

      await Promise.race([
        Promise.all(
          imgs.map(
            (img) =>
              new Promise((resolve) => {
                if (img.complete) return resolve(true);
                img.addEventListener("load", () => resolve(true), { once: true });
                img.addEventListener("error", () => resolve(true), { once: true });
              })
          )
        ),
        new Promise((resolve) => setTimeout(resolve, 2000)),
      ]);
    };

    const getOriginalNodes = () => {
      const children = Array.from(cards.children);
      if (children.length >= 2 && children.length % 2 === 0) {
        // Your Liquid duplicates once, so first half is "original"
        return children.slice(0, children.length / 2);
      }
      return children;
    };

    const clearCards = () => {
      while (cards.firstChild) cards.removeChild(cards.firstChild);
    };

    const appendClones = (nodes) => {
      nodes.forEach((n) => cards.appendChild(n.cloneNode(true)));
    };

    const measureSetBoundary = (segmentCount) => {
      // boundary = distance from first item to the first item of the next segment
      const first = cards.children[0];
      const boundaryNode = cards.children[segmentCount];
      if (!first || !boundaryNode) return 0;

      const a = first.getBoundingClientRect();
      const b = boundaryNode.getBoundingClientRect();
      return Math.round((b.left - a.left) * 1000) / 1000; // stable float
    };

    const buildTrack = () => {
      const originals = getOriginalNodes();
      if (!originals.length) return { segmentCount: 0, setWidth: 0 };

      clearCards();

      // 1) Build ONE segment by repeating originals until segment width >= viewport + one card
      const minCoverage = wrapper.clientWidth + getCardWidth();
      let segmentCount = 0;

      // Start with one copy of originals
      appendClones(originals);
      segmentCount += originals.length;

      // Keep extending the segment until it can cover the viewport smoothly
      // (prevents empty space on wide screens / low card count)
      let safety = 0;
      while (cards.scrollWidth < minCoverage && safety < 12) {
        appendClones(originals);
        segmentCount += originals.length;
        safety += 1;
      }

      // 2) Duplicate the whole segment once more (2 segments total)
      const segmentNodes = Array.from(cards.children).slice(0, segmentCount);
      appendClones(segmentNodes);

      // 3) Measure the exact boundary between segment1 and segment2
      const setWidth = measureSetBoundary(segmentCount);

      return { segmentCount, setWidth };
    };

    let state = { segmentCount: 0, setWidth: 0 };

    const applyTransform = () => {
      cards.style.transform = `translate3d(${-scrollPosition}px, 0, 0)`;
      cards.style.transition = "none";
      cards.style.willChange = "transform";
    };

    const tick = () => {
      if (!isUserInteracting || !isTouchDevice) {
        scrollPosition += scrollSpeed;

        const w = state.setWidth;
        if (w > 0 && scrollPosition >= w) {
          scrollPosition = scrollPosition % w;
        }

        applyTransform();
      }

      this.animationId = requestAnimationFrame(tick);
    };

    const start = async () => {
      await waitForImages();

      state = buildTrack();
      scrollPosition = 0;
      applyTransform();

      if (this.animationId) cancelAnimationFrame(this.animationId);
      tick();
    };

    // Rebuild on resize (important because your issue is viewport-width dependent)
    this._onResize = () => {
      if (this._resizeRaf) cancelAnimationFrame(this._resizeRaf);
      this._resizeRaf = requestAnimationFrame(() => start());
    };
    window.addEventListener("resize", this._onResize);

    // Touch drag on mobile
    if (isTouchDevice) {
      let isDragging = false;
      let startX = 0;
      let startY = 0;
      let startScrollPosition = 0;
      let swipeDirection = null;

      const handleTouchStart = (e) => {
        const touch = e.touches[0];
        startX = touch.pageX;
        startY = touch.pageY;
        startScrollPosition = scrollPosition;
        swipeDirection = null;
        clearTimeout(userInteractionTimeout);
      };

      const handleTouchMove = (e) => {
        const touch = e.touches[0];
        const deltaX = Math.abs(touch.pageX - startX);
        const deltaY = Math.abs(touch.pageY - startY);

        if (swipeDirection === null && (deltaX > 5 || deltaY > 5)) {
          swipeDirection = deltaX > deltaY ? "horizontal" : "vertical";
        }

        if (swipeDirection === "horizontal") {
          if (!isDragging) {
            isDragging = true;
            isUserInteracting = true;
          }

          e.preventDefault();

          const diff = startX - touch.pageX;
          scrollPosition = startScrollPosition + diff;

          const w = state.setWidth || 0;
          if (w > 0) {
            while (scrollPosition < 0) {
              scrollPosition += w;
              startScrollPosition += w;
            }
            while (scrollPosition >= w) {
              scrollPosition -= w;
              startScrollPosition -= w;
            }
          }

          applyTransform();
        }
      };

      const handleTouchEnd = () => {
        isDragging = false;
        swipeDirection = null;

        clearTimeout(userInteractionTimeout);
        userInteractionTimeout = setTimeout(() => {
          isUserInteracting = false;
        }, 1000);
      };

      wrapper.addEventListener("touchstart", handleTouchStart, { passive: true });
      wrapper.addEventListener("touchmove", handleTouchMove, { passive: false });
      wrapper.addEventListener("touchend", handleTouchEnd);
    }

    // Go
    start();
  }
}

customElements.define("happy-tummies", HappyTummies);
